import * as React from 'react';

import { AuiIf, ThreadPrimitive, useAuiState, useThreadViewport } from '@assistant-ui/react';
import { MessageSquareOffIcon } from 'lucide-react';
import { LazyMotion, MotionConfig, domAnimation } from 'motion/react';
import { useTranslations } from 'next-intl';

import { useConfigValue } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { useReadOnlyLoading } from '@/components/providers/read-only-runtime-provider';
import { CarouselUrlsProvider } from '@/components/ui/carousel-urls-context';
import { useBootRestoreWelcomeGate } from '@/hooks/use-boot-restore-welcome-gate';
import { ContentSettlementProvider, useSettlementToken } from '@/hooks/use-content-settlement';
import { useHostLanguageDetection } from '@/hooks/use-host-language-detection';
import { useParentUrlSuggestions } from '@/hooks/use-parent-url-suggestions';
import { useThreadSwitchBehavior } from '@/hooks/use-thread-switch-behavior';

import { threadIsNotEmpty } from './thread/aui-selectors';
import { Composer } from './thread/composer';
import { renderMessage } from './thread/message-routing';
import { BranchPicker, EditComposer, UserActionBar } from './thread/messages';
import { ThreadWelcome, ThreadWelcomeSuggestions } from './thread/welcome';

interface ThreadProps {
  /** Called when the settling state changes (for rendering an overlay in the parent). */
  onSettlingChange?: (settling: boolean) => void;
  /** Ref forwarded to the composer footer so the parent can read its height on demand. */
  composerRef?: React.Ref<HTMLDivElement>;
}

export function Thread({ onSettlingChange, composerRef }: ThreadProps = {}) {
  const threadId = useAuiState(({ threadListItem }) => threadListItem?.id);
  const messageCount = useAuiState(({ thread }) => thread.messages.length);
  const isRunning = useAuiState(({ thread }) => thread.isRunning);
  /**
   * Deliberately not `ThreadPrimitive.Empty`. That primitive gates on `thread.isEmpty`, which the
   * runtime derives from its RAW input array, while what actually renders comes from the message
   * clients. On boot those disagree — `isEmpty` reads false against zero rendered messages — and the
   * welcome silently never mounts. The rendered messages are the honest signal for "nothing to show".
   */
  const hasNoMessages = messageCount === 0;
  const t = useTranslations('screenReader');
  const isReadOnly = useReadOnly();
  const isLoadingMessages = useReadOnlyLoading();
  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;

  useHostLanguageDetection({ enabled: !isReadOnly });

  // Resumable tenants restore onto a persisted boot thread on any full load — a refresh or the
  // cross-tab reload; hold the welcome until its messages arrive so the empty thread doesn't flash
  // mid-restore. No-op (and byte-identical render) for non-resumable tenants.
  const suppressWelcomeForRestore = useBootRestoreWelcomeGate({
    enabled: resumableEnabled && !isReadOnly,
    messageCount,
    isRunning,
  });

  // Fade the restored conversation in (rather than popping) once the boot-restore lands — softens
  // the brief empty window of a refresh / cross-tab reload. Resumable-tenants-only; `undefined`
  // keeps the non-resumable render path byte-identical.
  const messageLogClassName = resumableEnabled
    ? `transition-opacity duration-300 ${suppressWelcomeForRestore ? 'opacity-0' : 'opacity-100'}`
    : undefined;

  const suggestionsVisible = !isReadOnly && !suppressWelcomeForRestore && hasNoMessages;
  const { pending: suggestionsRefreshing } = useParentUrlSuggestions({
    suggestionsVisible,
  });

  // Delay showing the empty state by one frame after loading finishes so the
  // runtime has time to propagate messages to subscribers.
  const [emptyGateOpen, setEmptyGateOpen] = React.useState(!isReadOnly);
  React.useEffect(() => {
    if (!isReadOnly) return;
    if (isLoadingMessages) {
      setEmptyGateOpen(false);
      return;
    }
    const id = requestAnimationFrame(() => setEmptyGateOpen(true));
    return () => cancelAnimationFrame(id);
  }, [isReadOnly, isLoadingMessages]);

  const viewportRef = React.useRef<HTMLDivElement>(null);
  const scrollToBottom = useThreadViewport(s => s.scrollToBottom);

  const { handleSettle, handleSubmit, handleScrollToBottom } = useThreadSwitchBehavior({
    threadId,
    isReadOnly,
    isRunning,
    viewportRef,
    scrollToBottom,
    onSettlingChange,
  });

  return (
    <LazyMotion features={domAnimation}>
      <MotionConfig reducedMotion="user">
        <CarouselUrlsProvider>
          <ThreadPrimitive.Root
            className="aui-root aui-thread-root flex h-full w-full flex-col bg-background"
            style={{
              ['--thread-max-width' as string]: '56rem',
            }}
            role="main"
            id="main-content"
            tabIndex={-1}
            aria-label={t('chatConversation')}
          >
            <ThreadPrimitive.Viewport
              ref={viewportRef}
              className="aui-thread-viewport flex flex-1 flex-col overflow-x-hidden overflow-y-scroll px-4"
              turnAnchor="top"
              autoScroll={false}
              scrollToBottomOnRunStart={false}
              scrollToBottomOnInitialize={!isReadOnly}
              scrollToBottomOnThreadSwitch={false}
            >
              <div className="mx-auto flex w-full max-w-[var(--thread-max-width)] flex-1 flex-col">
                {isReadOnly
                  ? emptyGateOpen && hasNoMessages && <ReadOnlyEmptyState />
                  : suggestionsVisible && (
                      <>
                        <ThreadWelcome />
                        <ThreadWelcomeSuggestions refreshing={suggestionsRefreshing} />
                      </>
                    )}

                {/* resetKey clears pending tokens on thread switch without remounting the subtree */}
                <ContentSettlementProvider resetKey={threadId} onSettle={handleSettle}>
                  {/* Keeps settlement pending while messages are still loading (read-only mode) */}
                  <MessageLoadingToken />
                  <div
                    role="log"
                    aria-live="polite"
                    aria-relevant="additions"
                    className={messageLogClassName}
                  >
                    <AuiIf condition={threadIsNotEmpty}>
                      <TopSpacer />
                    </AuiIf>
                    <ThreadPrimitive.Messages>{renderMessage}</ThreadPrimitive.Messages>
                    <AuiIf condition={threadIsNotEmpty}>
                      <div className="h-6 shrink-0" aria-hidden="true" />
                    </AuiIf>
                  </div>
                </ContentSettlementProvider>
              </div>
              {!isReadOnly && (
                <ThreadPrimitive.ViewportFooter
                  ref={composerRef}
                  className={`thread-viewport-footer scroll-fade-above sticky bottom-0 z-50 -mx-4 w-[calc(100%+2rem)] px-4 ${messageCount === 0 ? 'thread-empty' : ''}`}
                >
                  <Composer onSubmit={handleSubmit} onScrollToBottom={handleScrollToBottom} />
                </ThreadPrimitive.ViewportFooter>
              )}
            </ThreadPrimitive.Viewport>
          </ThreadPrimitive.Root>
        </CarouselUrlsProvider>
      </MotionConfig>
    </LazyMotion>
  );
}

function MessageLoadingToken() {
  const isLoadingMessages = useReadOnlyLoading();
  useSettlementToken(isLoadingMessages);
  return null;
}

function ReadOnlyEmptyState() {
  const t = useTranslations('readOnly');

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
      <MessageSquareOffIcon className="size-10 text-muted-foreground" />
      <div className="space-y-1">
        <p className="text-base font-medium text-muted-foreground">{t('noMessages')}</p>
        <p className="text-sm text-muted-foreground/70">{t('noMessagesDescription')}</p>
      </div>
    </div>
  );
}

function TopSpacer() {
  const register = useThreadViewport(s => s.registerContentInset);

  React.useEffect(() => {
    const handle = register();
    handle.setHeight(24);
    return () => handle.unregister();
  }, [register]);

  return <div className="shrink-0" aria-hidden="true" />;
}

export { EditComposer, BranchPicker, UserActionBar };
